import { PointTransaction, PointTransactionSource, PointTransactionType } from '@api/Customers/Customer';
import { Order, OrderStatus } from '@api/Orders/Order';
import { Reward } from '@api/Rewards/Reward';
import { getEarnedPoints } from '@utils/Order';

// ============================================================================
// RECONSTRUIR LA CARTERA DE PUNTOS — puro, y por fin bajo prueba.
//
// Esto es la **fuente de verdad del saldo de puntos de un cliente**: no suma ni
// resta sobre lo que había, sino que **rehace el historial entero** desde las
// órdenes que existen HOY. Por eso repara solo lo que se haya roto por el
// camino — un pedido borrado, unos puntos que se otorgaron dos veces, un canje
// que el sistema dejó pasar sin saldo.
//
// Corre en cuatro puntos del día a día: cada búsqueda de cliente en el POS, la
// identificación desde `/display`, cada login de la web y el checkout. Es lo que
// hace que **la cartera se auto-sane** y que no haga falta un rebuild global.
//
// ## Por qué se extrajo (2026-09-13)
// Vivía dentro de `CustomerServ.correctUsersPointsBasedOnOrders`: **176 líneas
// mezcladas con Firestore y sin una sola prueba**, en el código que decide
// cuántos puntos tiene cada cliente. Estaba anotado en el backlog como
// "candidato #1 a extraer" desde hacía semanas.
//
// Se extrajo ahora porque el aseo diario va a ejecutarlo **solo, de madrugada,
// sin nadie mirando** — y automatizar 176 líneas sin pruebas sobre las carteras
// de los clientes es justo el patrón que ya falló tres veces esta semana.
//
// ⚠️ **RÉPLICA DE PARIDAD** con krispis-web y krispis-server. Las tres leen y
// escriben los MISMOS documentos: si se separan, un cliente tendría un saldo
// distinto según por dónde entrara.
//
// ⚠️ **Las frases de las descripciones son parte del contrato**, no decoración:
// "saldo insuficiente" y "bienvenida" son cómo se reconocen las transacciones
// que hay que regenerar. Cambiar una palabra hace que el rebuild las conserve Y
// las vuelva a crear, duplicando puntos en cada corrida.
// ============================================================================

/** Lo mínimo que una fecha necesita para ordenar el historial. */
export interface ConSegundos {
    seconds?: number;
}

/** Un ajuste que el propio rebuild creó antes: se descarta y se regenera. */
export const isInsufficientBalanceAdjustment = (tx: PointTransaction): boolean =>
    typeof tx.description === 'string' && tx.description.includes('saldo insuficiente');

/**
 * El bono de bienvenida, en cualquiera de sus formatos históricos.
 *
 * Se reconoce para QUITARLO y volver a crearlo, que es lo que lo hace
 * idempotente. Los abonos manuales legítimos no usan la palabra "bienvenida".
 */
export const isWelcomeBonus = (tx: PointTransaction): boolean =>
    typeof tx.description === 'string' && tx.description.toLowerCase().includes('bienvenida');

/** Redondeo a 2 decimales: sin esto el saldo acumula ruido de punto flotante. */
export const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;

type LedgerEvent =
    | { seconds: number; kind: 'tx'; tx: PointTransaction }
    | {
          seconds: number;
          kind: 'order';
          date: unknown;
          orderId?: string;
          shortId: string;
          earned: number;
          spent: number;
      };

export interface WalletRebuilt {
    currentPoints: number;
    transactions: PointTransaction[];
}

export interface RebuildInput {
    /** Cuándo se dio de alta el cliente: la fecha del bono de bienvenida. */
    createdAt?: unknown;
    /** Lo que hay hoy en su cartera. */
    transactions: PointTransaction[];
    /** Sus órdenes que EXISTEN hoy (las borradas y canceladas ya no vienen). */
    orders: Order[];
    reward: Reward;
}

/**
 * Rehace la cartera de un cliente desde cero.
 *
 * El orden dentro de cada orden **no es un detalle**: primero el canje y después
 * la ganancia, evaluando el canje contra el saldo PREVIO. Si se hiciera al
 * revés, un cliente podría pagar con puntos que esa misma compra le acaba de
 * dar.
 */
export const rebuildWallet = (input: RebuildInput): WalletRebuilt => {
    const { createdAt, transactions, orders, reward } = input;

    // 1) Se conserva lo que NO viene de una orden, no es un ajuste que este
    //    mismo rebuild creó, y no es el bono de bienvenida (ése se regenera).
    const preservadas = (transactions || []).filter(
        (tx) =>
            tx.source !== PointTransactionSource.Order &&
            !isInsufficientBalanceAdjustment(tx) &&
            !isWelcomeBonus(tx)
    );

    const events: LedgerEvent[] = [];
    for (const tx of preservadas) {
        events.push({ seconds: (tx.date as ConSegundos)?.seconds ?? 0, kind: 'tx', tx });
    }

    for (const order of orders) {
        // La ganancia se otorga SOLO al completarse la orden; el canje se
        // descuenta desde que está pagada o en curso. Cuando la orden se
        // complete, el mismo evento sumará también su ganancia.
        const earned = order.status === OrderStatus.Completed ? getEarnedPoints(order, reward) : 0;
        const spent = round2(((order.pointsAmount || 0) / reward.reward.value) * reward.reward.costPoints);
        if (earned <= 0 && spent <= 0) continue;
        const date = order.date;
        events.push({
            seconds: (date as ConSegundos)?.seconds ?? 0,
            kind: 'order',
            date,
            orderId: order.id,
            shortId: order.shortId,
            earned,
            spent
        });
    }

    // 3) Cronológico. El sort es estable, así que en empate de fecha las
    //    conservadas van antes que las órdenes — mismo criterio que antes.
    events.sort((a, b) => a.seconds - b.seconds);

    const corregidas: PointTransaction[] = [];
    let balance = 0;

    // 0) El primer renglón siempre es el bono de bienvenida, si está activo.
    const welcomePoints = reward.newUser?.status ? round2(reward.newUser.points || 0) : 0;
    if (welcomePoints > 0) {
        corregidas.push({
            date: createdAt,
            points: welcomePoints,
            type: PointTransactionType.Initial,
            source: PointTransactionSource.System,
            description: `Se otorgaron ${welcomePoints} puntos de bienvenida`
        } as PointTransaction);
        balance = welcomePoints;
    }

    for (const event of events) {
        if (event.kind === 'tx') {
            const { tx } = event;
            corregidas.push(tx);
            balance =
                tx.type === PointTransactionType.Spent
                    ? round2(balance - round2(tx.points))
                    : round2(balance + tx.points);
            continue;
        }

        const { date, orderId, shortId, earned, spent } = event;
        let ajustada = false;

        if (spent > 0) {
            if (spent > balance) {
                // El sistema dejó canjear puntos que el cliente no tenía. Se
                // registra el faltante como ajuste para que el historial cuadre,
                // y esa compra NO otorga ganancia.
                const faltante = round2(spent - balance);
                corregidas.push({
                    date,
                    points: faltante,
                    type: PointTransactionType.Earned,
                    source: PointTransactionSource.System,
                    description: `Ajuste de ${faltante} puntos por saldo insuficiente${
                        shortId ? ` (orden ${shortId})` : ''
                    }`,
                    referenceId: orderId
                } as PointTransaction);
                balance = round2(balance + faltante);
                ajustada = true;
            }
            corregidas.push({
                date,
                points: spent,
                type: PointTransactionType.Spent,
                source: PointTransactionSource.Order,
                description: `Canje de ${spent} puntos en orden ${shortId}`,
                referenceId: orderId
            } as PointTransaction);
            balance = round2(balance - spent);
        }

        if (earned > 0 && !ajustada) {
            corregidas.push({
                date,
                points: earned,
                type: PointTransactionType.Earned,
                source: PointTransactionSource.Order,
                description: `Ganó ${earned.toFixed(2)} puntos por compra en orden ${shortId}`,
                referenceId: orderId
            } as PointTransaction);
            balance = round2(balance + earned);
        }
    }

    return { currentPoints: round2(balance), transactions: corregidas };
};
